import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuth from '../../auth/feature/auth';
import { ROUTER_KEYS } from '../consts/app-keys.const';
import { fetchLocalizedMarkdown } from '../utils/localized-markdown.util';

interface ChangeSection {
  type: string; // e.g. "Added", "Fixed", "Changed"
  items: string[];
}

interface ChangeVersion {
  version: string;
  date: string;
  sections: ChangeSection[];
}

export interface ProductChangelogProps {
  /** Display name, used in the subtitle (e.g. "TouchPose"). */
  productName: string;
  /** Current shipped version; entries newer than this are hidden. */
  version: string;
  /**
   * Path to the committed English changelog markdown, served from /public.
   * Translated copies are found automatically beside it (".de.md", ".zh.md",
   * and so on), with this file as the fallback when a language has none yet.
   */
  mdPath: string;
  /**
   * GitHub `owner/repo` whose release pages back the per-version download links
   * (e.g. "squarebit-studios/touchpose-public"). When null, every download link
   * points at My Products instead (for products with no public release repo).
   */
  publicRepo?: string | null;
  /**
   * Whether to show the per-version download link at all. Defaults to true.
   * Set false for products that aren't publicly downloadable yet (no public
   * release repo and no general availability), so the changelog is read-only.
   */
  showDownloadLinks?: boolean;
  /**
   * Platforms to offer per version, for products whose releases are NOT public.
   * Given together with `onDownloadVersion`, each version renders one small
   * link per platform instead of a single link to a release page: a private
   * product has no public release page, and the bytes have to come through a
   * gated endpoint that can report a 403 rather than a browser error page.
   */
  downloadPlatforms?: {
    os: string;
    label: string;
    /** Platform mark shown inside the pill, e.g. a Windows or Apple glyph. */
    icon?: React.ReactNode;
  }[];
  onDownloadVersion?: (version: string, os: string) => void;
  /**
   * `<version>-<os>` of the download in flight, so that button can
   * show it and refuse a second click. These archives take seconds to
   * arrive, and a button that looks idle invites clicking again.
   */
  downloadingKey?: string | null;
}

// Returns true if version a is <= version b (semver x.y.z)
function semverLte(a: string, b: string): boolean {
  const parse = (v: string) => v.split('.').map((n) => parseInt(n, 10) || 0);
  const [a1, a2, a3] = parse(a);
  const [b1, b2, b3] = parse(b);
  if (a1 !== b1) return a1 < b1;
  if (a2 !== b2) return a2 < b2;
  return a3 <= b3;
}

function parseChangelog(md: string): ChangeVersion[] {
  const versions: ChangeVersion[] = [];
  let currentVersion: ChangeVersion | null = null;
  let currentSection: ChangeSection | null = null;
  // Items array of the current bullet context, so a wrapped (multi-line) bullet
  // can have its continuation lines appended onto the last item.
  let activeItems: string[] | null = null;

  for (const raw of md.split('\n')) {
    const line = raw.trim();

    // ## [1.2.3] - 2024-01-15  or  ## 1.2.3 - 2024-01-15  or  ## v1.2.3
    const versionMatch = line.match(
      /^##\s+\[?v?([\d.]+(?:-\w+)?)\]?(?:\s+-\s+(.+))?/
    );
    if (versionMatch) {
      if (currentSection && currentVersion)
        currentVersion.sections.push(currentSection);
      if (currentVersion) versions.push(currentVersion);
      currentSection = null;
      activeItems = null;
      currentVersion = {
        version: versionMatch[1],
        date: versionMatch[2]?.trim() ?? '',
        sections: []
      };
      continue;
    }

    // ### Added / ### Fixed / etc.
    const sectionMatch = line.match(/^###\s+(.+)/);
    if (sectionMatch && currentVersion) {
      if (currentSection) currentVersion.sections.push(currentSection);
      currentSection = { type: sectionMatch[1].trim(), items: [] };
      activeItems = currentSection.items;
      continue;
    }

    // - bullet item (start of a list item)
    const bulletMatch = line.match(/^[-*]\s+(.+)/);
    if (bulletMatch && currentVersion) {
      if (currentSection) {
        activeItems = currentSection.items;
      } else {
        // Bullets without a section header — put in a default section
        let defaultSection = currentVersion.sections.find(
          (s) => s.type === 'Changes'
        );
        if (!defaultSection) {
          defaultSection = { type: 'Changes', items: [] };
          currentVersion.sections.push(defaultSection);
        }
        activeItems = defaultSection.items;
      }
      activeItems.push(bulletMatch[1]);
      continue;
    }

    // Continuation line of a wrapped bullet — the markdown source hard-wraps
    // long bullets across several indented lines; join them onto the last item.
    if (line && activeItems && activeItems.length > 0) {
      activeItems[activeItems.length - 1] += ' ' + line;
    }
  }

  // Flush last section + version
  if (currentSection && currentVersion) currentVersion.sections.push(currentSection);
  if (currentVersion) versions.push(currentVersion);

  // Strip markdown bold from the assembled (possibly multi-line) items.
  for (const v of versions) {
    for (const s of v.sections) {
      s.items = s.items.map((it) => it.replace(/\*\*(.+?)\*\*/g, '$1'));
    }
  }

  return versions;
}

const SECTION_COLORS: Record<string, { bg: string; text: string }> = {
  Added: { bg: 'var(--badge-green-bg)', text: 'var(--badge-green-text)' },
  Fixed: { bg: 'var(--badge-yellow-bg)', text: 'var(--badge-yellow-text)' },
  Changed: { bg: 'var(--badge-blue-bg)', text: 'var(--badge-blue-text)' },
  Removed: { bg: 'var(--badge-red-bg)', text: 'var(--badge-red-text)' },
  Deprecated: { bg: 'var(--badge-purple-bg)', text: 'var(--badge-purple-text)' },
  Security: { bg: 'var(--badge-rose-bg)', text: 'var(--badge-rose-text)' },
  Changes: { bg: 'var(--color-bg-secondary)', text: 'var(--color-slate-500)' },
  Notes: { bg: 'var(--color-bg-secondary)', text: 'var(--color-slate-500)' }
};

/** Download arrow, nudged down on hover so the pill feels like an action. */
const DownloadArrow = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 14 14"
    fill="none"
    aria-hidden="true"
    className="shrink-0 transition-transform group-hover:translate-y-[2px]"
  >
    <path
      d="M7 1.75v7.5M3.5 5.75L7 9.25l3.5-3.5M2.5 12.25h9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function sectionStyle(type: string) {
  return (
    SECTION_COLORS[type] ?? {
      bg: 'var(--color-bg-secondary)',
      text: 'var(--color-slate-500)'
    }
  );
}

const SECTION_TYPE_KEYS: Record<string, string> = {
  Added: 'changelog.sectionAdded',
  Fixed: 'changelog.sectionFixed',
  Changed: 'changelog.sectionChanged',
  Removed: 'changelog.sectionRemoved',
  Deprecated: 'changelog.sectionDeprecated',
  Security: 'changelog.sectionSecurity',
  Changes: 'changelog.sectionChanges',
  // Both of these appear in shipped changelogs (### Improved in the Subdivs
  // markdown, ### Site in the TouchPose one). Without a key they fell through
  // to the raw English heading in every language, English included.
  Improved: 'changelog.sectionImproved',
  Site: 'changelog.sectionSite',
  Infrastructure: 'changelog.sectionInfrastructure',
  // Context that is not itself a change - e.g. the Eye 1.14.0 note that
  // pipeline builds are unaffected by licensing. Registered rather than left
  // to fall through, for the reason above it.
  Notes: 'changelog.sectionNotes'
};

/**
 * Product-agnostic changelog page. Every product renders the same way; the
 * differences (which markdown, which version cutoff, which public release repo)
 * come in as props, so adding a product is just a thin wrapper.
 */
export const ProductChangelog: React.FC<ProductChangelogProps> = ({
  productName,
  version,
  mdPath,
  publicRepo,
  showDownloadLinks = true,
  downloadPlatforms,
  onDownloadVersion,
  downloadingKey = null
}) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [versions, setVersions] = useState<ChangeVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const language = i18n.resolvedLanguage || i18n.language;

  useEffect(() => {
    // Reload on language change: the changelog body is prose, so it comes from
    // a translated markdown file rather than from the i18n resource bundle.
    let cancelled = false;
    setLoading(true);
    setError('');

    // Keyed to the shipped version, so a release fetches fresh notes rather
    // than a copy the browser cached before that release existed.
    fetchLocalizedMarkdown(mdPath, language, version)
      .then((text) => {
        if (cancelled) return;
        const all = parseChangelog(text);
        setVersions(all.filter((v) => semverLte(v.version, version)));
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(t('changelog.error'));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };

    // out deliberately; it changes identity on a language switch, which
    // `language` already keys this effect on.
  }, [mdPath, version, language]);

  if (loading) {
    return (
      <div
        className="font-body text-slate-400"
        style={{ padding: 'clamp(24px, 5vw, 40px) clamp(20px, 5.5vw, 72px)' }}
      >
        {t('changelog.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="font-body text-red-500"
        style={{ padding: 'clamp(24px, 5vw, 40px) clamp(20px, 5.5vw, 72px)' }}
      >
        {error}
      </div>
    );
  }

  return (
    <div
      className="font-body max-w-[860px]"
      style={{ padding: 'clamp(24px, 5vw, 40px) clamp(20px, 5.5vw, 72px)' }}
    >
      <h1 className="font-display text-[28px] font-extrabold text-content-primary mb-[8px] tracking-[-0.5px]">
        {t('changelog.title')}
      </h1>
      <p className="text-[14px] text-slate-400 mb-[40px]">
        {t('changelog.subtitle', { product: productName })}
      </p>

      <div className="flex flex-col gap-[32px]">
        {versions.map((v, i) => {
          // Signed-in customers go to My Products (their licensed download).
          // Otherwise, link the public release page if the product has one;
          // products with no public repo always route to My Products.
          const toManagement = i === 0 && isAuthenticated;
          return (
            <div key={i} className="border-l-2 border-border pl-[24px]">
              {/* Version header */}
              <div className="flex items-baseline gap-[12px] mb-[16px]">
                <span className="font-display text-[18px] font-bold text-content-primary">
                  v{v.version}
                </span>
                {v.date && (
                  <span className="text-[13px] text-slate-400">{v.date}</span>
                )}
                {!showDownloadLinks ? null : onDownloadVersion &&
                  downloadPlatforms?.length ? (
                  <span className="ml-auto flex items-center gap-[8px]">
                    {downloadPlatforms.map((platform) => (
                      <button
                        key={platform.os}
                        type="button"
                        disabled={
                          downloadingKey === `${v.version}-${platform.os}`
                        }
                        onClick={() => onDownloadVersion(v.version, platform.os)}
                        title={t('changelog.downloadFor', {
                          platform: platform.label,
                          version: v.version
                        })}
                        className={`group inline-flex items-center gap-[6px] px-[11px] py-[5px] rounded-[999px] text-[12.5px] font-medium bg-surface-section border border-border-dark/[0.14] text-content-secondary transition-all ${
                          downloadingKey === `${v.version}-${platform.os}`
                            ? 'opacity-50 cursor-wait'
                            : 'hover:border-blue hover:text-blue hover:bg-surface-hover cursor-pointer'
                        }`}
                      >
                        {platform.icon}
                        {platform.label}
                        <DownloadArrow />
                      </button>
                    ))}
                  </span>
                ) : toManagement || !publicRepo ? (
                  <Link
                    to={ROUTER_KEYS.MANAGEMENT}
                    className="ml-auto text-[13px] font-medium text-blue-link no-underline hover:underline"
                  >
                    {t('changelog.downloadLink')}
                  </Link>
                ) : (
                  <a
                    href={`https://github.com/${publicRepo}/releases/tag/v${v.version}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto text-[13px] font-medium text-blue-link no-underline hover:underline"
                  >
                    {t('changelog.downloadLink')} ↗
                  </a>
                )}
              </div>

              {/* Sections */}
              <div className="flex flex-col gap-[16px]">
                {v.sections.map((sec, j) => {
                  const colors = sectionStyle(sec.type);
                  return (
                    <div key={j}>
                      <span
                        className="inline-block text-[11px] font-bold tracking-[0.06em] uppercase rounded-[4px] px-[8px] py-[2px] mb-[10px]"
                        style={{ background: colors.bg, color: colors.text }}
                      >
                        {SECTION_TYPE_KEYS[sec.type]
                          ? t(SECTION_TYPE_KEYS[sec.type])
                          : sec.type}
                      </span>
                      <ul className="m-0 pl-[22px] list-disc marker:text-slate-500 flex flex-col gap-[14px]">
                        {sec.items.map((item, k) => (
                          <li
                            key={k}
                            className="text-[14px] leading-[1.65] text-content-secondary"
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProductChangelog;
